// Une carte du « Suivi des élèves » du dashboard coach web, simplifiée mais
// fidèle : identité, semaines consécutives, dernière séance, 13 dernières
// semaines, puis les quatre indicateurs (régularité, séances par semaine,
// progression, régression). Seuils de couleur et calculs repris de
// pages/CoachMode.js (getRegularityColor, calculateStreak…).
import React from "react";

const VERT = "#4ade80";
const JAUNE = "#facc15";
const ORANGE = "#F87B1B";
const ROUGE = "#ef4444";

const couleurRegularite = (s) => (s >= 75 ? VERT : s >= 60 ? JAUNE : s >= 50 ? ORANGE : ROUGE);
const couleurProgression = (s) => (s >= 50 ? VERT : s >= 35 ? JAUNE : s >= 20 ? ORANGE : ROUGE);
const couleurRegression = (s) => (s <= 10 ? VERT : s <= 25 ? JAUNE : s <= 40 ? ORANGE : ROUGE);
const couleurSeances = (m) => (m >= 3 ? VERT : m >= 2 ? JAUNE : m >= 1 ? ORANGE : ROUGE);
const couleurJours = (j) => (j <= 3 ? "#27ae60" : j <= 6 ? "#f39c12" : "#e74c3c");

// Semaines consécutives avec au moins une séance, en partant de la plus récente.
function serie(semaines) {
	let n = 0;
	for (let i = semaines.length - 1; i >= 0 && semaines[i] > 0; i--) n++;
	return n;
}

// Six premières semaines contre six dernières, comme getRegularityTrend.
function tendance(semaines) {
	const moy = (t) => t.reduce((a, b) => a + b, 0) / t.length;
	const avant = moy(semaines.slice(0, 6));
	const apres = moy(semaines.slice(-6));
	if (apres > avant + 0.2) return { texte: "↑ En hausse", couleur: VERT };
	if (apres < avant - 0.2) return { texte: "↓ En baisse", couleur: ROUGE };
	return { texte: "→ Stable", couleur: "rgba(255,255,255,0.45)" };
}

function Barre({ valeur, couleur, i }) {
	return (
		<span className="gs-ce-barre">
			<span className="gs-ce-remplissage" style={{ width: `${valeur}%`, background: couleur, "--i": i }} />
		</span>
	);
}

export default function CarteEleve({ eleve, className = "" }) {
	const e = eleve;
	const s = serie(e.semaines);
	const t = tendance(e.semaines);
	const max = Math.max(...e.semaines, 1);
	const inactif = e.joursDepuis > 7;
	const seances = e.seances.toFixed(1);

	return (
		<div className={`gs-ce${inactif ? " gs-ce--inactif" : ""} ${className}`.trim()}>
			<div className="gs-ce-tete">
				<span className="gs-ce-avatar">{e.initiales}</span>
				<div style={{ minWidth: 0 }}>
					<div className="gs-ce-nom">{e.nom}</div>
					<div className="gs-ce-sous">{e.entrainements} entraînements</div>
				</div>
			</div>

			<div className="gs-ce-serie">
				<span aria-hidden="true">🔥</span>
				<b>{s}</b>
				<span>sem. consécutive{s !== 1 ? "s" : ""}</span>
			</div>

			<div>
				<span className="gs-ce-badge" style={{ background: couleurJours(e.joursDepuis) }}>
					{e.joursDepuis === 0 ? "Aujourd'hui" : `Il y a ${e.joursDepuis} j`}
				</span>
			</div>

			<div className="gs-ce-semaines">
				<div className="gs-ce-lbl">13 dernières semaines</div>
				<div className="gs-ce-batons">
					{e.semaines.map((v, i) => (
						<span
							key={i}
							style={{
								height: Math.max(2, (v / max) * 24),
								background: v > 0 ? "#30a49a" : "rgba(255,255,255,0.1)",
								"--i": i,
							}}
						/>
					))}
				</div>
			</div>

			<div className="gs-ce-ligne">
				<span className="gs-ce-lbl">Régularité</span>
				<Barre valeur={e.regularite} couleur={couleurRegularite(e.regularite)} i={0} />
				<b style={{ color: couleurRegularite(e.regularite) }}>{e.regularite}</b>
				<span className="gs-ce-tendance" style={{ color: t.couleur }}>
					{t.texte}
				</span>
			</div>

			<div className="gs-ce-ligne">
				<span className="gs-ce-lbl">Séances/sem</span>
				<b style={{ color: couleurSeances(e.seances) }}>{seances}</b>
			</div>

			<div className="gs-ce-ligne">
				<span className="gs-ce-lbl">Progression</span>
				<Barre valeur={e.progression} couleur={couleurProgression(e.progression)} i={1} />
				<b style={{ color: couleurProgression(e.progression) }}>{e.progression}%</b>
				<span className="gs-ce-detail">
					{e.progressees}/{e.analysees}
				</span>
			</div>

			<div className="gs-ce-ligne">
				<span className="gs-ce-lbl">Régression</span>
				<Barre valeur={e.regression} couleur={couleurRegression(e.regression)} i={2} />
				<b style={{ color: couleurRegression(e.regression) }}>{e.regression}%</b>
				<span className="gs-ce-detail">
					{e.regressees}/{e.analysees}
				</span>
			</div>
		</div>
	);
}
